import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  inject,
  Input,
  OnInit,
  Optional,
  Output,
  Renderer2,
  Self,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControlDirective, NgControl } from '@angular/forms';
import { asyncScheduler, fromEvent, ReplaySubject, Subject } from 'rxjs';

import { InputTypeService } from '../../services/input-type.service';
import { EnhancedFormControl } from '../form-extensions/generic-form';
import { AppIcon } from '../icons/icon.type';
import { AppIconsLibrary } from '../icons/icons-library.service';
import { InputComponent } from '../input/input.component';
import { ControlClassSync } from './control-class-sync';
import { DropdownBase } from './dropdown.base';
import { menuTransition } from './dropdown.transitions';
import { SelectInputHost, SelectValue } from './select-item.directive';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-dropdown-select',
  template: `
    @if (title) {
      <div class="dpw-title secondary" [ngClass]="{ 'margin-bottom-zero': searchAllowed && !$searchDisabled() }">
        <span>{{ title }}</span>
        <app-icon
          [icon]="closeIcon"
          dpwClose
          buttonTap
          highlightStyle="dark"
          [highlightInset]="16"
          [closeMode]="'all'"></app-icon>
      </div>
    }
    <div *ngIf="searchAllowed && !$searchDisabled()" class="search-field">
        <!-- [placeholder]="'SHARED.BTN.SEARCH' | translate" -->
      <app-input
        class="solid full-width"
        [extras]="['clear', 'search']"
        [placeholder]="'SHARED.BTN.SEARCH'"
        [formControl]="control"
        #searchInputRef></app-input>
    </div>
    <ng-content select=".dpw-header"></ng-content>
    <div
      id="app-scroll"
      class="data-host native-scroll"
      [ngClass]="{ 'no-search': !searchAllowed || $searchDisabled(), 'with-title': !!title }">
      <ng-content></ng-content>
    </div>
    <ng-content select=".dpw-scroll"></ng-content>
    <ng-content select=".dpw-footer"></ng-content>
  `,
  styleUrl: './styles/dropdown-select.scss',
  animations: [menuTransition],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownSelectComponent implements OnInit, ControlValueAccessor, SelectInputHost {
  readonly closeIcon?: AppIcon;

  control = new EnhancedFormControl('');

  @Input()
  title?: string;

  @ViewChild('searchInputRef')
  searchInputRef!: InputComponent;

  get value(): SelectValue {
    return this.currentValue;
  }

  get searchAllowed(): boolean {
    return this.searchSubj.observers.length > 0;
  }

  @Input()
  multi?: boolean;

  private changedSubj = new ReplaySubject<SelectValue>(1);
  @Output()
  changed = this.changedSubj.asObservable();

  private searchSubj = new Subject<string>();
  @Output()
  search = this.searchSubj.asObservable();

  protected $searchDisabled: WritableSignal<boolean | undefined> = signal(undefined);

  private currentValue: SelectValue = null;
  private onChange: (_: SelectValue) => void = () => {};
  private onTouched: () => void = () => {};
  private destroyRef = inject(DestroyRef);
  private controlClassSync: ControlClassSync;
  private icons = inject(AppIconsLibrary);

  constructor(
    input: InputTypeService,
    public dropdown: DropdownBase,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private doc: Document,
    @Self() @Optional() ngControl?: NgControl,
    @Self() @Optional() public controlDirective?: FormControlDirective,
  ) {
    this.controlClassSync = new ControlClassSync(this.destroyRef, this.renderer);
    this.closeIcon = this.icons.getIcon('close');
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
    this.dropdown.opened.pipe(takeUntilDestroyed()).subscribe(() => {
      if (!input.isTouch$.getValue()) {
        asyncScheduler.schedule(() => {
          this.dropdown.stopClose();
          if (this.searchInputRef) {
            this.searchInputRef.focus();
          }
        });
      }
    });
    fromEvent<KeyboardEvent>(this.doc, 'keydown')
      .pipe(takeUntilDestroyed())
      .subscribe(event => {
        if (this.doc.activeElement !== this.searchInputRef?.inputRef.nativeElement) {
          return;
        }
        if (event.key === 'Enter') {
          this.searchInputRef.blur();
        }
      });
  }

  ngOnInit(): void {
    if (this.searchSubj.observers.length) {
      this.control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
        this.searchSubj.next(v);
      });
    }
    this.dropdown.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.onTouched();
      this.control.setValue('');
    });
    if (this.controlDirective && this.dropdown.anchorRef) {
      this.controlClassSync.sync(this.dropdown.anchorRef, this.controlDirective);
    }
  }

  onItemSelected(value: SelectValue): void {
    this.updateValue(value);
    this.onChange(this.currentValue);
    if (!this.multi) {
      this.dropdown.scheduleClose();
    }
  }

  private updateValue(value: SelectValue): void {
    if (this.multi) {
      this.currentValue = this.currentValue || [];
      if (value && Array.isArray(this.currentValue) && !Array.isArray(value)) {
        const foundIndex = this.currentValue.findIndex(v => v === value);
        if (foundIndex >= 0) {
          this.currentValue.splice(foundIndex, 1);
        } else {
          this.currentValue.push(value);
        }
      }
    } else {
      this.currentValue = value;
    }
    const input = this.dropdown.anchorRef?.input;
    if (input && input.inputRef.nativeElement.tagName !== 'DIV') {
      if (Array.isArray(this.currentValue)) {
        input.writeValue(this.currentValue?.join(', '));
      } else if (!this.multi) {
        input.writeValue(this.currentValue?.toString());
      }
    }
    this.changedSubj.next(this.currentValue);
  }

  setSearchDisabled(disabled?: boolean) {
    this.$searchDisabled.set(disabled);
  }

  // ControlValueAccessor
  writeValue(value: object): void {
    this.currentValue = this.multi && !Array.isArray(value) ? (value ? [value] : []) : value;
    this.updateValue(value);
  }

  registerOnChange(fn: (val: SelectValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (!this.dropdown.anchorRef) {
      return;
    }
    this.dropdown.isEnabled = !isDisabled;
    if (isDisabled) {
      this.renderer.setAttribute(this.dropdown.anchorRef?.element, 'disabled', '');
    } else {
      this.renderer.removeAttribute(this.dropdown.anchorRef?.element, 'disabled');
    }
  }
}
